import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { VirtualItem } from '@tanstack/react-virtual';
import { useVirtualizer } from '@tanstack/react-virtual';
import { twMerge } from 'tailwind-merge';

import { Spinner } from '@/_components';
import { NoDataMessage } from '@/_containers';

type BodyScrollVirtualizedListProps<T> = {
  data: T[];
  hasMore: boolean;
  isLoading: boolean;
  fetchMore: () => void;
  renderItem: (item: T) => React.ReactNode;
  estimatedSize?: number | ((index: number) => number);
  emptyMessage: string;
  className?: string;
  overscan?: number;
};

export function BodyScrollVirtualizedList<T>({
  data,
  hasMore,
  isLoading,
  fetchMore,
  renderItem,
  estimatedSize = 100,
  emptyMessage,
  className,
  overscan = 5,
}: BodyScrollVirtualizedListProps<T>) {
  const fetchSentRef = useRef<boolean>(false);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Use body scroll for virtualization
  const virtualizer = useVirtualizer({
    count: hasMore ? data.length + 1 : data.length,
    getScrollElement: () => (typeof window !== 'undefined' ? window.document.documentElement : null),
    estimateSize: typeof estimatedSize === 'function' ? estimatedSize : () => estimatedSize,
    overscan,
    gap: 8,
  });

  const virtualizedItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const isEmpty = data.length === 0;

  // Intersection Observer for infinite loading
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && !fetchSentRef.current) {
          fetchSentRef.current = true;
          fetchMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px', // Start loading 100px before the trigger is visible
      }
    );

    observer.observe(loadMoreTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, fetchMore]);

  // Reset fetch flag when new data arrives
  useEffect(() => {
    if (!isLoading) {
      fetchSentRef.current = false;
    }
  }, [isLoading]);

  const renderVirtualItem = useCallback(
    (virtualItem: VirtualItem) => {
      const isLoaderRow = virtualItem.index > data.length - 1;
      const item = data[virtualItem.index];

      return (
        <div
          key={virtualItem.key}
          data-index={virtualItem.index}
          ref={virtualizer.measureElement}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: `${virtualItem.size}px`,
            transform: `translateY(${virtualItem.start}px)`,
          }}
        >
          {isLoaderRow ? (
            <div
              ref={loadMoreTriggerRef}
              className={twMerge('flex h-16 w-full items-center justify-center', !isLoading && 'hidden')}
            >
              <Spinner size="xs" />
            </div>
          ) : (
            renderItem(item)
          )}
        </div>
      );
    },
    [data, renderItem, virtualizer.measureElement, isLoading]
  );

  if (isEmpty) {
    return (
      <div className="mt-6 w-full px-6">
        <NoDataMessage message={emptyMessage} />
      </div>
    );
  }

  return (
    <div className={twMerge('w-full', className)}>
      <div
        className="relative w-full"
        style={{
          height: `${totalSize}px`,
        }}
      >
        {virtualizedItems.map(renderVirtualItem)}
      </div>

      {/* Hidden trigger for intersection observer when no loader row is rendered */}
      {hasMore && !isLoading && data.length > 0 && (
        <div ref={loadMoreTriggerRef} className="pointer-events-none h-1 w-full opacity-0" aria-hidden="true" />
      )}
    </div>
  );
}
